import type { DetectedVendor } from '../types.js';

/**
 * Seed-Wissensbasis fuer Drittanbieter.
 *
 * Wichtig und ehrlich: das ist eine Startmenge, keine vollstaendige Datenbank.
 * Cookiebots Vorsprung besteht genau hier. Die Liste waechst ueber Scans:
 * unbekannte Hosts landen als "unclassified" im Dashboard und werden nach
 * Pruefung hier ergaenzt.
 *
 * "category" ist die uebliche Einordnung, keine Rechtsauskunft. Der Mandant
 * kann pro Site abweichen (z. B. ein Dienst, der dort tatsaechlich notwendig ist).
 */
export const VENDORS: DetectedVendor[] = [
  // ---------- Statistik / Analyse ----------
  {
    key: 'google_analytics',
    name: 'Google Ireland Limited (Google Analytics)',
    category: 'statistics',
    hosts: ['google-analytics.com', 'analytics.google.com'],
    privacyUrl: 'https://policies.google.com/privacy',
    country: 'IE/US',
  },
  {
    key: 'google_tag_manager',
    name: 'Google Ireland Limited (Tag Manager)',
    category: 'statistics',
    hosts: ['googletagmanager.com'],
    privacyUrl: 'https://policies.google.com/privacy',
    country: 'IE/US',
  },
  {
    key: 'matomo',
    name: 'Matomo',
    category: 'statistics',
    hosts: ['matomo.cloud', 'matomo.org'],
    privacyUrl: 'https://matomo.org/privacy-policy/',
    country: 'DE/NZ',
  },
  {
    key: 'hotjar',
    name: 'Hotjar Ltd.',
    category: 'statistics',
    hosts: ['hotjar.com', 'hotjar.io'],
    privacyUrl: 'https://www.hotjar.com/legal/policies/privacy/',
    country: 'MT',
  },
  {
    key: 'microsoft_clarity',
    name: 'Microsoft Corporation (Clarity)',
    category: 'statistics',
    hosts: ['clarity.ms'],
    privacyUrl: 'https://privacy.microsoft.com/privacystatement',
    country: 'US',
  },
  {
    key: 'plausible',
    name: 'Plausible Insights OU',
    category: 'statistics',
    hosts: ['plausible.io'],
    privacyUrl: 'https://plausible.io/privacy',
    country: 'EE',
  },
  {
    key: 'mixpanel',
    name: 'Mixpanel Inc.',
    category: 'statistics',
    hosts: ['mixpanel.com', 'mxpnl.com'],
    privacyUrl: 'https://mixpanel.com/legal/privacy-policy/',
    country: 'US',
  },
  {
    key: 'amplitude',
    name: 'Amplitude Inc.',
    category: 'statistics',
    hosts: ['amplitude.com'],
    privacyUrl: 'https://amplitude.com/privacy',
    country: 'US',
  },
  {
    key: 'segment',
    name: 'Twilio Inc. (Segment)',
    category: 'statistics',
    hosts: ['segment.com', 'segment.io'],
    privacyUrl: 'https://www.twilio.com/legal/privacy',
    country: 'US',
  },

  // ---------- Marketing / Werbung ----------
  {
    key: 'google_ads',
    name: 'Google Ireland Limited (Google Ads)',
    category: 'marketing',
    hosts: ['googleadservices.com', 'googlesyndication.com', 'doubleclick.net'],
    privacyUrl: 'https://policies.google.com/privacy',
    country: 'IE/US',
  },
  {
    key: 'meta',
    name: 'Meta Platforms Ireland Limited',
    category: 'marketing',
    hosts: ['facebook.com', 'facebook.net', 'connect.facebook.net', 'fbcdn.net', 'instagram.com'],
    privacyUrl: 'https://www.facebook.com/privacy/policy',
    country: 'IE/US',
  },
  {
    key: 'linkedin',
    name: 'LinkedIn Ireland Unlimited Company',
    category: 'marketing',
    hosts: ['linkedin.com', 'licdn.com', 'snap.licdn.com'],
    privacyUrl: 'https://www.linkedin.com/legal/privacy-policy',
    country: 'IE/US',
  },
  {
    key: 'tiktok',
    name: 'TikTok Technology Limited',
    category: 'marketing',
    hosts: ['tiktok.com', 'analytics.tiktok.com', 'tiktokcdn.com'],
    privacyUrl: 'https://www.tiktok.com/legal/privacy-policy-eea',
    country: 'IE/CN',
  },
  {
    key: 'microsoft_ads',
    name: 'Microsoft Corporation (Bing Ads / UET)',
    category: 'marketing',
    hosts: ['bat.bing.com', 'bing.com'],
    privacyUrl: 'https://privacy.microsoft.com/privacystatement',
    country: 'US',
  },
  {
    key: 'pinterest',
    name: 'Pinterest Europe Ltd.',
    category: 'marketing',
    hosts: ['pinterest.com', 'pinimg.com', 'ct.pinterest.com'],
    privacyUrl: 'https://policy.pinterest.com/privacy-policy',
    country: 'IE/US',
  },
  {
    key: 'x_twitter',
    name: 'Twitter International Unlimited Company (X)',
    category: 'marketing',
    hosts: ['twitter.com', 'x.com', 'ads-twitter.com', 't.co'],
    privacyUrl: 'https://x.com/de/privacy',
    country: 'IE/US',
  },
  {
    key: 'hubspot',
    name: 'HubSpot Inc.',
    category: 'marketing',
    hosts: ['hubspot.com', 'hs-scripts.com', 'hsforms.net', 'hsadspixel.net', 'hubapi.com'],
    privacyUrl: 'https://legal.hubspot.com/privacy-policy',
    country: 'US/IE',
  },

  // ---------- Einbettungen / gemischt ----------
  {
    key: 'youtube',
    name: 'Google Ireland Limited (YouTube)',
    category: 'marketing',
    hosts: ['youtube.com', 'youtube-nocookie.com', 'ytimg.com'],
    privacyUrl: 'https://policies.google.com/privacy',
    country: 'IE/US',
  },
  {
    key: 'vimeo',
    name: 'Vimeo.com Inc.',
    category: 'statistics',
    hosts: ['vimeo.com', 'vimeocdn.com'],
    privacyUrl: 'https://vimeo.com/privacy',
    country: 'US',
  },
  {
    key: 'google_maps',
    name: 'Google Ireland Limited (Maps)',
    category: 'preferences',
    hosts: ['maps.google.com', 'maps.googleapis.com'],
    privacyUrl: 'https://policies.google.com/privacy',
    country: 'IE/US',
  },
  {
    key: 'google_fonts',
    name: 'Google Ireland Limited (Fonts)',
    category: 'preferences',
    hosts: ['fonts.googleapis.com', 'fonts.gstatic.com'],
    privacyUrl: 'https://policies.google.com/privacy',
    country: 'IE/US',
  },
  {
    key: 'intercom',
    name: 'Intercom R&D Unlimited Company',
    category: 'preferences',
    hosts: ['intercom.io', 'intercomcdn.com', 'intercomassets.com'],
    privacyUrl: 'https://www.intercom.com/legal/privacy',
    country: 'IE/US',
  },

  // ---------- Notwendig / Infrastruktur ----------
  {
    key: 'cloudflare',
    name: 'Cloudflare Inc.',
    category: 'necessary',
    hosts: ['cloudflare.com', 'challenges.cloudflare.com'],
    privacyUrl: 'https://www.cloudflare.com/privacypolicy/',
    country: 'US/EU',
  },
  {
    key: 'stripe',
    name: 'Stripe Payments Europe Ltd.',
    category: 'necessary',
    hosts: ['stripe.com', 'js.stripe.com', 'm.stripe.network'],
    privacyUrl: 'https://stripe.com/privacy',
    country: 'IE/US',
  },
  {
    key: 'paypal',
    name: 'PayPal (Europe) S.a r.l. et Cie, S.C.A.',
    category: 'necessary',
    hosts: ['paypal.com', 'paypalobjects.com'],
    privacyUrl: 'https://www.paypal.com/de/webapps/mpp/ua/privacy-full',
    country: 'LU',
  },
  {
    key: 'recaptcha',
    name: 'Google Ireland Limited (reCAPTCHA)',
    category: 'necessary',
    hosts: ['recaptcha.net'],
    privacyUrl: 'https://policies.google.com/privacy',
    country: 'IE/US',
  },
];

const HOST_INDEX = new Map<string, DetectedVendor>();
for (const vendor of VENDORS) {
  for (const host of vendor.hosts) HOST_INDEX.set(host.toLowerCase(), vendor);
}

/** Findet den Anbieter zu einem Host, inklusive Subdomains. */
export function vendorForHost(host: string): DetectedVendor | undefined {
  const clean = host.toLowerCase().replace(/^\.+/, '').replace(/:[0-9]+$/, '');
  const direct = HOST_INDEX.get(clean);
  if (direct) return direct;
  for (const [pattern, vendor] of HOST_INDEX) {
    if (clean === pattern || clean.endsWith('.' + pattern)) return vendor;
  }
  return undefined;
}

export function vendorByKey(key: string): DetectedVendor | undefined {
  return VENDORS.find((v) => v.key === key);
}
